import React, { useEffect, useState } from 'react';
import { Calendar } from 'lucide-react';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import { useTheme } from '@emotion/react';

const DateRangeSelector = ({ onDateRangeChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const handleApply = () => {
    if (startDate && endDate) {
      if (new Date(endDate) < new Date(startDate)) {
        alert('End Date cannot be earlier than Start Date');
        return;
      }
      onDateRangeChange({ start: startDate, end: endDate });
      setIsOpen(false);
    } else {
      alert('Please select both start and end dates');
    }
  };

  const handdleDateChange = (type, value) => {
    if (type === 'start') {
      setStartDate(value);
    } else {
      setEndDate(value);
    }
  };

  useEffect(() => {
  }, [startDate, endDate]);

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={clsx(
          "flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors",
          {
            "bg-gray-800 border-gray-700 text-gray-100 hover:bg-gray-700": isDark,
            "bg-white border-gray-200 text-gray-900 hover:bg-gray-50": !isDark,
          }
        )}
      >
        <Calendar className={clsx("w-4 h-4", { "text-gray-100": isDark, "text-gray-800": !isDark })} />
        <span className="text-sm">
          {startDate && endDate 
            ? `${new Date(startDate).toLocaleDateString()} - ${new Date(endDate).toLocaleDateString()}` 
            : 'Select Date Range'}
        </span>
      </button>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: isOpen ? 1 : 0, y: isOpen ? 0 : 10 }}
        transition={{ duration: 0.3 }}
        className={clsx(
          "absolute top-12 left-0 z-50 w-60 p-4 rounded-lg shadow-lg border",
          {
            "bg-gray-800 border-gray-700 text-gray-100": isDark,
            "bg-white border-gray-200 text-gray-900": !isDark,
          }
        )}
      >
        {isOpen && (
          <div className="flex flex-col gap-4">
            <div className="space-y-2">
              <label className={clsx("block text-sm font-medium", { "text-gray-300": isDark, "text-gray-700": !isDark })}>
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className={clsx(
                  "w-full px-3 py-2 rounded-lg border",
                  {
                    "bg-gray-700 text-gray-100 focus:ring-blue-500 focus:ring-2": isDark,
                    "bg-white text-gray-900 focus:ring-blue-500 focus:ring-2": !isDark,
                  }
                )}
              />
            </div>

            <div className="space-y-2">
              <label className={clsx("block text-sm font-medium", { "text-gray-300": isDark, "text-gray-700": !isDark })}>
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className={clsx(
                  "w-full px-3 py-2 rounded-lg border",
                  {
                    "bg-gray-700 text-gray-100 focus:ring-blue-500 focus:ring-2": isDark,
                    "bg-white text-gray-900 focus:ring-blue-500 focus:ring-2": !isDark,
                  }
                )}
              />
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setIsOpen(false)}
                className={clsx(
                  "px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors",
                  {
                    "bg-gray-700 text-gray-100 hover:bg-gray-600": isDark,
                    "bg-gray-100 text-gray-900 hover:bg-gray-200": !isDark,
                  }
                )}
              >
                Cancel
              </button>
              <button
                onClick={handleApply}
                className={clsx(
                  "px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors",
                  {
                    "bg-blue-600 text-white hover:bg-blue-700": isDark,
                    "bg-blue-600 text-white hover:bg-blue-700": !isDark,
                  }
                )}
              >
                Apply
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default DateRangeSelector;
